import type { JSX } from 'react';
import { useState } from 'react';
import { PublicError, takeTokenFromUrl, verifyAccount } from '../../data/public';
import { FormError, PublicCard } from './PublicCard';

/**
 * The API refuses a used, expired or unknown token with one message on
 * purpose -- distinguishing them would tell a guesser which tokens exist. So
 * the page offers the likely explanations instead of knowing which applies.
 */
const STALE_LINK_HINT =
  'If you have already confirmed this address, the link will not work a second time — just sign in. ' +
  'Links also stop working after seven days.';

/**
 * `/verify?token=…` — the owner confirms the address they registered with.
 *
 * **A button, not an automatic request on load.** Mail security scanners open
 * links to check them, and some run the page's script when they do. The token
 * is single-use, so a scanner that redeemed it on arrival would leave the
 * owner holding a dead link and an account stuck at pending.
 */
export function Verify(): JSX.Element {
  const [token] = useState(takeTokenFromUrl);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmedName, setConfirmedName] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    if (token === null) return;
    setBusy(true);
    setError(null);
    try {
      const { account } = await verifyAccount(token);
      setConfirmedName(account.name);
    } catch (err) {
      setError(err instanceof PublicError ? err.message : 'Something went wrong. Try again.');
      setBusy(false);
    }
  }

  if (confirmedName !== null) {
    return (
      <PublicCard subtitle="Address confirmed">
        <p className="invite__preamble">
          {confirmedName} is set up. Sign in to invite your clinicians and create cohorts.
        </p>
        <a className="primary signin__submit" href="/">
          Sign in
        </a>
      </PublicCard>
    );
  }

  if (token === null) {
    return (
      <PublicCard subtitle="Confirm your email address">
        <p className="invite__preamble">
          This link is incomplete. Open the link from your email again, or copy the whole of it
          into the address bar.
        </p>
      </PublicCard>
    );
  }

  return (
    <PublicCard subtitle="Confirm your email address">
      <FormError message={error === null ? null : `${error} ${STALE_LINK_HINT}`} />
      <p className="invite__preamble">
        Confirm this address to finish setting up your organisation.
      </p>
      <button
        type="button"
        className="primary signin__submit"
        disabled={busy}
        onClick={() => void confirm()}
      >
        {busy ? 'Confirming…' : 'Confirm my email address'}
      </button>
    </PublicCard>
  );
}
